import React, { useState, useEffect, useContext, useMemo } from 'react';
import axios from 'axios';
import { useParams, Link } from 'react-router-dom';
import { SiteSettingsContext } from './PublicLayout';
import { ArrowLeft, Briefcase, MapPin, CheckCircle2, Loader2, Calendar, Clock } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/** Groups slot objects by YYYY-MM-DD (local TZ) for a clean day picker. */
function groupSlotsByDay(slots) {
  const byDay = {};
  slots.forEach(s => {
    const d = new Date(s.starts_at);
    const key = d.toLocaleDateString(undefined, { year: 'numeric', month: '2-digit', day: '2-digit' });
    if (!byDay[key]) byDay[key] = [];
    byDay[key].push(s);
  });
  return Object.entries(byDay)
    .sort(([a], [b]) => new Date(a) - new Date(b))
    .map(([day, items]) => ({
      day,
      label: new Date(items[0].starts_at).toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' }),
      items,
    }));
}

export default function PublicApply() {
  const { jobId } = useParams();
  const settings = useContext(SiteSettingsContext) || {};
  const primary = settings.primary_color || '#1a3a5c';

  const [job, setJob] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState({});
  const [resumeFile, setResumeFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [step, setStep] = useState('form'); // form | schedule | done
  const [applicantId, setApplicantId] = useState(null);
  const [errorMsg, setErrorMsg] = useState('');
  const [slots, setSlots] = useState(null);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [booking, setBooking] = useState(false);
  const [bookedAt, setBookedAt] = useState(null);

  useEffect(() => {
    axios.get(`${API}/public/job-positions/${jobId}`).then(r => setJob(r.data)).catch(() => setJob(false));
    axios.get(`${API}/public/application-questions`).then(r => setQuestions(r.data));
  }, [jobId]);

  const setAnswer = (label, value) => setAnswers(prev => ({ ...prev, [label]: value }));

  const validate = () => {
    for (const q of questions) {
      if (q.required) {
        if (q.field_type === 'file') {
          if (!resumeFile) return `Please upload your ${q.label}.`;
        } else {
          const v = answers[q.label];
          if (v === undefined || v === '' || (Array.isArray(v) && v.length === 0)) {
            return `Please fill in "${q.label}".`;
          }
        }
      }
    }
    return null;
  };

  const submitApplication = async (e) => {
    e.preventDefault();
    setErrorMsg('');
    const err = validate();
    if (err) { setErrorMsg(err); return; }
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append('position_id', jobId);
      fd.append('answers_json', JSON.stringify(answers));
      if (resumeFile) fd.append('resume', resumeFile);
      const res = await axios.post(`${API}/public/apply`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setApplicantId(res.data.applicant_id);
      setStep('schedule');
      // Kick off slot loading
      setLoadingSlots(true);
      const slotRes = await axios.get(`${API}/public/interview-slots`, { params: { position_id: jobId } });
      setSlots(slotRes.data);
    } catch (e2) {
      setErrorMsg(e2.response?.data?.detail || 'Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
      setLoadingSlots(false);
    }
  };

  const bookSlot = async () => {
    if (!selectedSlot) return;
    setBooking(true);
    try {
      await axios.post(`${API}/public/book-interview`, {
        applicant_id: applicantId,
        interviewer_id: selectedSlot.interviewer_id,
        starts_at: selectedSlot.starts_at,
        ends_at: selectedSlot.ends_at,
      });
      setBookedAt(new Date(selectedSlot.starts_at));
      setStep('done');
    } catch (e) {
      alert(e.response?.data?.detail || 'Booking failed. Someone may have grabbed that slot — please pick another.');
      // Refresh slots
      const slotRes = await axios.get(`${API}/public/interview-slots`, { params: { position_id: jobId } });
      setSlots(slotRes.data);
      setSelectedSlot(null);
    } finally {
      setBooking(false);
    }
  };

  const groupedSlots = useMemo(() => (slots ? groupSlotsByDay(slots) : []), [slots]);

  if (job === false) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-16 text-center">
        <h2 className="text-2xl font-bold mb-2">Position not found</h2>
        <p className="text-muted-foreground mb-6">This position may have been filled or removed.</p>
        <Link to="/careers" className="text-primary underline">← Back to open positions</Link>
      </div>
    );
  }
  if (!job) return <div className="p-10 text-center text-muted-foreground">Loading…</div>;

  return (
    <div className="max-w-3xl mx-auto px-4 py-10">
      <Link to="/careers" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground mb-4">
        <ArrowLeft size={14} /> Back to careers
      </Link>

      {/* Progress */}
      <div className="flex items-center gap-2 mb-6 text-xs">
        <span className={`px-2 py-1 rounded ${step === 'form' ? 'bg-primary text-white font-semibold' : 'bg-muted'}`}>1. Application</span>
        <span className="text-muted-foreground">→</span>
        <span className={`px-2 py-1 rounded ${step === 'schedule' ? 'bg-primary text-white font-semibold' : step === 'done' ? 'bg-emerald-100 text-emerald-700 font-semibold' : 'bg-muted'}`}>2. Schedule Interview</span>
        <span className="text-muted-foreground">→</span>
        <span className={`px-2 py-1 rounded ${step === 'done' ? 'bg-emerald-100 text-emerald-700 font-semibold' : 'bg-muted'}`}>3. Done</span>
      </div>

      {/* Job header */}
      <div className="bg-white rounded-xl border p-5 mb-6">
        <h1 className="text-2xl font-bold" style={{ color: primary }} data-testid="apply-job-title">{job.title}</h1>
        <div className="flex items-center gap-4 text-sm text-muted-foreground mt-1.5 flex-wrap">
          {job.discipline && <span className="inline-flex items-center gap-1"><Briefcase size={13} /> {job.discipline}</span>}
          {job.location && <span className="inline-flex items-center gap-1"><MapPin size={13} /> {job.location}</span>}
          {job.employment_type && <span className="text-xs bg-muted px-2 py-0.5 rounded">{job.employment_type}</span>}
        </div>
        {job.description && <p className="mt-4 text-sm whitespace-pre-wrap leading-relaxed">{job.description}</p>}
        {job.requirements && (
          <div className="mt-4 pt-4 border-t">
            <h4 className="text-sm font-semibold mb-1">Requirements</h4>
            <p className="text-sm text-muted-foreground whitespace-pre-wrap">{job.requirements}</p>
          </div>
        )}
      </div>

      {/* Step 1 — form */}
      {step === 'form' && (
        <form onSubmit={submitApplication} className="bg-white rounded-xl border p-5 space-y-4" data-testid="application-form">
          <h2 className="text-lg font-semibold" style={{ color: primary }}>Apply</h2>
          {questions.map(q => (
            <div key={q.id}>
              <label className="text-sm font-medium block mb-1">
                {q.label}
                {q.required && <span className="text-red-500 ml-0.5">*</span>}
              </label>
              {q.field_type === 'textarea' ? (
                <textarea
                  rows={4}
                  placeholder={q.placeholder}
                  value={answers[q.label] || ''}
                  onChange={(e) => setAnswer(q.label, e.target.value)}
                  className="w-full rounded-md border border-input px-3 py-2 text-sm resize-y"
                  data-testid={`apply-field-${q.id}`}
                />
              ) : q.field_type === 'select' ? (
                <select
                  value={answers[q.label] || ''}
                  onChange={(e) => setAnswer(q.label, e.target.value)}
                  className="w-full h-10 rounded-md border border-input px-3 py-2 text-sm"
                  data-testid={`apply-field-${q.id}`}
                >
                  <option value="">— Select —</option>
                  {(q.options || []).map(o => <option key={o} value={o}>{o}</option>)}
                </select>
              ) : q.field_type === 'file' ? (
                <input
                  type="file"
                  accept=".pdf,.doc,.docx,.png,.jpg,.jpeg"
                  onChange={(e) => setResumeFile(e.target.files?.[0] || null)}
                  className="w-full text-sm"
                  data-testid={`apply-field-${q.id}`}
                />
              ) : q.field_type === 'checkbox' ? (
                <div className="flex flex-col gap-1">
                  {(q.options || []).map(o => {
                    const cur = answers[q.label] || [];
                    const active = cur.includes(o);
                    return (
                      <label key={o} className="flex items-center gap-2 text-sm">
                        <input
                          type="checkbox"
                          checked={active}
                          onChange={() => {
                            const next = active ? cur.filter(x => x !== o) : [...cur, o];
                            setAnswer(q.label, next);
                          }}
                        />
                        {o}
                      </label>
                    );
                  })}
                </div>
              ) : (
                <input
                  type={q.field_type === 'email' ? 'email' : q.field_type === 'phone' ? 'tel' : 'text'}
                  placeholder={q.placeholder}
                  value={answers[q.label] || ''}
                  onChange={(e) => setAnswer(q.label, e.target.value)}
                  className="w-full h-10 rounded-md border border-input px-3 py-2 text-sm"
                  data-testid={`apply-field-${q.id}`}
                />
              )}
            </div>
          ))}

          {errorMsg && <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded p-2">{errorMsg}</div>}

          <button
            type="submit"
            disabled={submitting}
            className="w-full h-11 rounded-md font-semibold text-white hover:opacity-90 transition-opacity disabled:opacity-60"
            style={{ backgroundColor: primary }}
            data-testid="submit-application-btn"
          >
            {submitting ? <Loader2 className="animate-spin mx-auto" size={18} /> : 'Submit Application →'}
          </button>
        </form>
      )}

      {/* Step 2 — schedule */}
      {step === 'schedule' && (
        <div className="bg-white rounded-xl border p-5">
          <div className="flex items-center gap-2 mb-3">
            <CheckCircle2 className="text-emerald-500" size={20} />
            <h2 className="text-lg font-semibold" style={{ color: primary }}>Application received! Now pick a time for your first interview</h2>
          </div>
          <p className="text-sm text-muted-foreground mb-5">
            Slots are shown in your local timezone. Pick any 30-minute slot that works — we'll send a confirmation to your email.
          </p>
          {loadingSlots ? (
            <div className="py-10 text-center text-muted-foreground flex items-center justify-center">
              <Loader2 className="animate-spin mr-2" size={18} /> Loading open times…
            </div>
          ) : !slots || slots.length === 0 ? (
            <div className="py-8 text-center text-muted-foreground">
              No open interview slots are available right now. We'll reach out to you by email to schedule manually.
              <div className="mt-4">
                <Link to="/" className="text-primary underline text-sm">← Return home</Link>
              </div>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-h-[500px] overflow-y-auto pr-1">
                {groupedSlots.map(g => (
                  <div key={g.day} className="border rounded-md">
                    <div className="px-3 py-2 bg-muted/50 text-sm font-semibold flex items-center gap-2">
                      <Calendar size={14} /> {g.label}
                    </div>
                    <div className="p-2 flex flex-wrap gap-1.5">
                      {g.items.map(s => {
                        const when = new Date(s.starts_at).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
                        const selected = selectedSlot?.starts_at === s.starts_at && selectedSlot?.interviewer_id === s.interviewer_id;
                        return (
                          <button
                            key={s.starts_at + s.interviewer_id}
                            onClick={() => setSelectedSlot(s)}
                            className={`px-3 py-1.5 text-xs rounded-md border transition-colors ${
                              selected ? 'text-white font-semibold' : 'bg-background hover:bg-muted'
                            }`}
                            style={selected ? { backgroundColor: primary, borderColor: primary } : {}}
                            data-testid={`slot-${s.interviewer_id}-${s.starts_at}`}
                          >
                            <Clock size={11} className="inline mr-1" />
                            {when} · {s.interviewer_name}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>

              <button
                onClick={bookSlot}
                disabled={!selectedSlot || booking}
                className="w-full mt-6 h-11 rounded-md font-semibold text-white hover:opacity-90 transition-opacity disabled:opacity-60"
                style={{ backgroundColor: primary }}
                data-testid="confirm-booking-btn"
              >
                {booking ? <Loader2 className="animate-spin mx-auto" size={18} /> : selectedSlot ? 'Confirm Interview' : 'Pick a time to continue'}
              </button>
            </>
          )}
        </div>
      )}

      {/* Step 3 — done */}
      {step === 'done' && bookedAt && (
        <div className="bg-white rounded-xl border p-8 text-center">
          <CheckCircle2 className="text-emerald-500 mx-auto mb-4" size={48} />
          <h2 className="text-2xl font-bold mb-2" style={{ color: primary }}>You're all set!</h2>
          <p className="text-muted-foreground mb-1">Your interview is confirmed for</p>
          <p className="text-xl font-semibold mb-4">
            {bookedAt.toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric' })}
            {' at '}
            {bookedAt.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })}
          </p>
          <p className="text-sm text-muted-foreground mb-6">
            A confirmation email is on its way. We're looking forward to speaking with you!
          </p>
          <Link to="/" className="text-primary underline text-sm">← Return home</Link>
        </div>
      )}
    </div>
  );
}
